
import { useEffect, useMemo, useState } from 'react';

import groupBy from 'lodash/groupBy';

import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd';
import { isEmpty, set } from 'lodash';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';

import { MOBILE_MEDIA_POINT, Routes } from '@share/constants';
import { getQuotes, markQuotesAsRead } from '@share/store/slices';
import { RootState, getKeyQuotesFromPath, LoginType, isQuotesFromPath } from '@share/utils';
import { QuoteModalCompare, SearchTable } from '@components';
import { IColumnMenu, NULL_VALUE } from '@constants';
import { QuoteCarModalCompare } from '../quote-car-modal-compare';
import { QuoteCondosModalCompare } from '../quote-condos-modal-compare';
import { CARS_KEY, GetMyQuotesTabs, GetQuoteDatasourceData, HISTORICAL_KEY, HOTELS_KEY } from './utils';

import './style.scss';

export function MyQuotes() {
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const quoteStore = useSelector((state: RootState) => state.quoteStore);
  const navigationMenuStore = useSelector((state: RootState) => state.navigationMenuStore);
  
  const { items } = navigationMenuStore;
  const { account, user } = loginStore;
  const { loading, result } = quoteStore;

  const { quotesHotels, quotesCondos,quotesCars, quotesHistorical } = result;

  const [visibleHotels, setVisibleHotels] = useState(false);
  const [visibleCondos, setVisibleCondos] = useState(false);
  const [visibleCars, setVisibleCars] = useState(false);
  const [quoteBase, setQuoteBase] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(HOTELS_KEY);

  const isMobile = document.body.offsetWidth <= MOBILE_MEDIA_POINT;
  
  const dispatch = useDispatch();

  const history = useHistory();

  if (!account || account.type === LoginType.Public) {
    history.push(Routes.NotAuthorized);
  }

  useEffect(() => {
    if (!loginStore.loading) {
      const param = getKeyQuotesFromPath();
      setQuoteBase(param);
      dispatch(getQuotes(param));

      const isQuotes = isQuotesFromPath(history);
      if (isQuotes && param) {
        dispatch(markQuotesAsRead(param));
      }
    }
  }, [loginStore.loading])

  useEffect(() => {
    if (!!quoteStore.result.selectedQuotes?.length) {
      const quoteType = quoteStore.result.selectedQuotes[0].quoteType;
      if (quoteType === 'Condo') {
        setVisibleCondos(true);
      } else if (quoteType === 'Hotel') {
        setVisibleHotels(true);
      } else if (quoteType === 'Car') {
        setVisibleCars(true);
      }
    }
  }, [quoteStore.result.selectedQuotes])

  const handleOnRefresh = () => {
    if (user) {
      dispatch(getQuotes());
    }
  };

  const dataSourceHotels = useMemo(() => {
    if (!quotesHotels.length) return [];
    const grouped = groupBy(quotesHotels, quote => quote.quoteBaseId);
    return Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0]));
  }, [quotesHotels]);

  const dataSourceCondos = useMemo(() => {
    if (!quotesCondos.length) return [];
    const grouped = groupBy(quotesCondos, quote => quote.quoteBaseId);
    return Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quotesCondos));
  }, [quotesCondos]);

  const dataSourceCars = useMemo(() => {
    if (!quotesCars.length) return [];
    const grouped = groupBy(quotesCars, quote => quote.quoteBaseId);
    return Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quotesCars));
  }, [quotesCars]);

  const dataSourceHistorical = useMemo(() => {
    if (!quotesHistorical.length) return [];
    const grouped = groupBy(quotesHistorical, quote => quote.quoteBaseId);
    return Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quotesHistorical));
  }, [quotesHistorical]);

  const isCarsShareCompareEnabled = useMemo(() => account?.carsShareCompareEnabled && 
                                        (!account?.isRSITemplate || ((!account?.carsShareCompareRsiSiteidList?.length && !account?.carsShareCompareRsiOrgidList?.length) || 
                                        (account?.carsShareCompareRsiSiteidList?.includes(items?.siteId?.toString() || '') || account?.carsShareCompareRsiOrgidList?.includes(items?.organizationId?.toString() || '')))), [account, items]);

  const tabs = useMemo(() => {
    const allTabs = GetMyQuotesTabs(isMobile, dataSourceHotels, dataSourceCondos, dataSourceCars, dataSourceHistorical, setVisibleHotels, setVisibleCondos, setVisibleCars, setQuoteBase, (quoteId) => {
      dispatch(markQuotesAsRead(quoteId));
    });
    if (!isCarsShareCompareEnabled) {
      return allTabs.filter(tab => tab.key !== CARS_KEY);
    }
    return allTabs;
  }, [isMobile, dataSourceHotels, dataSourceCondos, dataSourceCars, dataSourceHistorical, isCarsShareCompareEnabled, dispatch]);

  const buttonTextColor = account?.buttonTextColor;
  const buttonBorder = account?.buttonBorder;
  const buttonBorderRadius = account?.buttonBorderRadius;
  const buttonColor = account?.buttonColor;

  const styleButtonBorderRadius = !isEmpty(buttonBorderRadius) ? { borderRadius: `${buttonBorderRadius}px` } : {};  
  const styleButtonBorder = !isEmpty(buttonBorder)? { ...styleButtonBorderRadius, border: buttonBorder } : { ...styleButtonBorderRadius };  
  const styleButtonColor = !isEmpty(buttonTextColor)? { ...styleButtonBorder, color: buttonTextColor } : { ...styleButtonBorder };  
  const styleButton = !isEmpty(buttonColor)? { ...styleButtonColor, background: buttonColor } : { ...styleButtonColor };  

  return (
    <div className={`quotes-wrapper ${isMobile ? 'mobile' : ''}`}>
      <div className="quotes-wrapper__refresh" onClick={handleOnRefresh}>
        <div className="action-button" style={styleButton}>
          <FontAwesomeIcon icon={faSyncAlt} size="1x"/>
          <span>Refresh</span>
        </div>
      </div>

      <Tabs defaultActiveKey={HOTELS_KEY} onChange={(key) => setActiveTab(key)}>
        {tabs.map(({ title, columns, datasource, key }) => (
          <Tabs.TabPane key={key} tab={<FormattedMessage id={title} />}>
            <SearchTable
              title={NULL_VALUE}
              loading={loading}
              actions={NULL_VALUE}
              allColumns={columns as IColumnMenu[]}
              dataSource={datasource}
              pagination={{
                showSizeChanger: false,
                showQuickJumper: false
              }}
              filters={NULL_VALUE}
              totalOrderCount={datasource.length}
              rowKey={(row: any) => row.key ? `${row.key}` : `${row.quoteBase}`}
              hideRefresh={true}
              isFilterApplied={false}
              onFiltersChange={NULL_VALUE}
              onTableChange={NULL_VALUE}
              onResetFilters={NULL_VALUE}
              onApplyFilters={NULL_VALUE}
              onRefresh={() => {}}
            ></SearchTable>
          </Tabs.TabPane>
        ))}
      </Tabs>

      {(quotesHotels.length || quotesHistorical.length) && visibleHotels ? 
        <QuoteModalCompare 
          visible={visibleHotels} 
          quoteBase={quoteBase as string} 
          onCancel={() => { setVisibleHotels(false); setQuoteBase(null) }} 
        /> : null}

      {(quotesCondos.length || quotesHistorical.length) && visibleCondos ? 
        <QuoteCondosModalCompare
          visible={visibleCondos} 
          isHistoricalTab={activeTab === HISTORICAL_KEY}
          quoteBase={quoteBase as string} 
          onCancel={() => { setVisibleCondos(false); setQuoteBase(null) }} 
        /> : null}

      {(quotesCars.length || quotesHistorical.length) && visibleCars ? 
        <QuoteCarModalCompare 
          visible={visibleCars} 
          quoteBase={quoteBase as string} 
          onCancel={() => { setVisibleCars(false); setQuoteBase(null) }} 
        /> : null}  
    </div>

  );
}
